import { Icon } from "@elaniintc/goes-design-system";
import { useMetricsPermissions } from "../hooks/use-metrics-perms";
import CardFallback from "@/components/ui/cardFallback";
import Card from "@/components/ui/card";
import type { DashboardData } from "@/types/common/dashboard";

const Loading = () => {
  return (
    <Icon
      type="far"
      name="spinner-third"
      className="text-action-quiet animate-spin text-lg"
    />
  );
};

interface RequestsRowProps {
  isLoading: boolean;
  isFetching: boolean;
  data: DashboardData | undefined;
  refetch: () => void;
}

export const RequestsRow = ({
  isLoading,
  isFetching,
  data,
  refetch,
}: RequestsRowProps) => {
  const { canSeeFRRow, hasInProgress, hasSolved, hasPending } =
    useMetricsPermissions();

  if (!canSeeFRRow) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <h6
          data-testid="requests-title"
          className="text-heading text-h6 font-semibold"
        >
          Trámites
        </h6>
        <button
          onClick={() => refetch()}
          className="text-action text-sm font-semibold cursor-pointer hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          type="button"
          disabled={isFetching}
        >
          Actualizar
        </button>
      </div>
      <div
        data-testid="requests-cards"
        className="grid grid-cols-1 tablet:grid-cols-3 gap-3"
      >
        {hasInProgress ? (
          <Card
            data-testid="in-process-card"
            className="flex flex-col items-start gap-1"
          >
            <span className="text-body font-normal">
              Solicitudes en proceso
            </span>
            <div className="flex items-center gap-2">
              <Icon
                type="far"
                name="loader"
                className="!text-action-quiet text-h5"
              />
              {isLoading ? (
                <Loading />
              ) : (
                <span className="text-heading text-h5 font-semibold">
                  {data?.data.formality_requests.in_process_qty ?? "-"}
                </span>
              )}
            </div>
          </Card>
        ) : (
          <CardFallback
            data-testid="in-process-card-fallback"
            className="flex justify-center items-center gap-4 shadow-none"
          >
            <span className="text-center font-normal text-on-disabled-quiet">
              Sin permiso para ver estos datos
            </span>
          </CardFallback>
        )}
        {hasSolved ? (
          <Card
            data-testid="done-card"
            className="flex flex-col items-start gap-1"
          >
            <span className="text-body font-normal">Solicitudes resueltas</span>
            <div className="flex items-center gap-2">
              <Icon
                type="far"
                name="circle-check"
                className="!text-success-quiet text-h5"
              />
              {isLoading ? (
                <Loading />
              ) : (
                <span className="text-heading text-h5 font-semibold">
                  {data?.data.formality_requests.done_qty ?? "-"}
                </span>
              )}
            </div>
          </Card>
        ) : (
          <CardFallback
            data-testid="done-card-fallback"
            className="flex justify-center items-center gap-4 shadow-none"
          >
            <span className="text-center font-normal text-on-disabled-quiet">
              Sin permiso para ver estos datos
            </span>
          </CardFallback>
        )}
        {hasPending ? (
          <Card
            data-testid="pending-card"
            className="flex flex-col items-start gap-1"
          >
            <span className="text-body font-normal">
              Solicitudes pendientes
            </span>
            <div className="flex items-center gap-2">
              <Icon
                type="far"
                name="clock"
                className="!text-warning-quiet text-h5"
              />
              {isLoading ? (
                <Loading />
              ) : (
                <span className="text-heading text-h5 font-semibold">
                  {data?.data.formality_requests.pending_qty ?? "-"}
                </span>
              )}
            </div>
          </Card>
        ) : (
          <CardFallback
            data-testid="pending-card-fallback"
            className="flex justify-center items-center gap-4 shadow-none"
          >
            <span className="text-center font-normal text-on-disabled-quiet">
              Sin permiso para ver estos datos
            </span>
          </CardFallback>
        )}
      </div>
    </div>
  );
};
